import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Remove as MinimizeIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  AudioFile as AudioIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as DocumentIcon,
  PlayArrow as PlayIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSocketContext } from "../../../context/SocketContext";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { axiosInstance } from "src/network/adapter";
import { IMAGE_URL, MEDIA_URL, VIDEO_URL, AUDIO_URL, ApiEndPoints } from "src/network/endpoints";
import EditGroupDialog from "./EditGroupDialog";

// Google Maps libraries - must be constant to prevent reloads
const GOOGLE_MAPS_LIBRARIES = ["places"];

// Styled components for the group info dialog
const Backdrop = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 1299,
  backdropFilter: "blur(4px)",
}));

const GroupInfoDialog = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  maxWidth: "800px",
  height: "80vh",
  maxHeight: "600px",
  backgroundColor: "#FFFFFF",
  borderRadius: "16px",
  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
  zIndex: 1300,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    width: "95vw",
    height: "90vh",
    maxHeight: "none",
  },
}));

const GroupInfoHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#7C3AED",
  color: "#FFFFFF",
  borderRadius: "16px 16px 0 0",
}));

const GroupInfoTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.75rem",
  fontWeight: 700,
  color: "#FFFFFF",
}));

const HeaderActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  "& .MuiIconButton-root": {
    color: "#FFFFFF",
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
    },
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  overflow: "hidden",
}));

const NavigationSidebar = styled(Box)(({ theme }) => ({
  width: "140px",
  backgroundColor: "#F8FAFC",
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const NavItem = styled(Box)(({ theme, active }) => ({
  padding: theme.spacing(2, 2.5),
  marginBottom: theme.spacing(1),
  borderRadius: theme.spacing(1.5),
  cursor: "pointer",
  backgroundColor: active ? "#7C3AED" : "transparent",
  color: active ? "#FFFFFF" : "#374151",
  fontWeight: active ? 600 : 500,
  fontSize: "0.9rem",
  transition: "all 0.2s ease",
  textAlign: "center",
  "&:hover": {
    backgroundColor: active ? "#7C3AED" : "#E5E7EB",
  },
}));

const MainContent = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  overflowY: "auto",
  backgroundColor: "#FFFFFF",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#1F2937",
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(1),
  borderBottom: `2px solid #7C3AED`,
}));

const MemberItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  "&:last-child": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: "#F8FAFC",
  },
}));

const MemberAvatar = styled(Avatar)(({ theme }) => ({
  width: 56,
  height: 56,
  marginRight: theme.spacing(2),
  border: `3px solid #7C3AED`,
  position: 'relative',
  '& .fallback-initials': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#7C3AED',
    borderRadius: '50%',
    transition: 'all 0.2s ease-in-out',
    userSelect: 'none',
    cursor: 'default',
    '&:hover': {
      backgroundColor: '#E5E7EB',
      transform: 'scale(1.02)',
      boxShadow: '0 2px 8px rgba(124, 58, 237, 0.2)',
    }
  },
  [theme.breakpoints.down('sm')]: {
    width: 48,
    height: 48,
    marginRight: theme.spacing(1.5),
    '& .fallback-initials': {
      fontSize: '1.25rem',
    }
  },
  [theme.breakpoints.down('xs')]: {
    width: 44,
    height: 44,
    marginRight: theme.spacing(1),
    '& .fallback-initials': {
      fontSize: '1.125rem',
    }
  }
}));

const MemberInfo = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const MemberName = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 500,
  color: "#1F2937",
  marginBottom: theme.spacing(0.5),
}));

const MemberSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  color: "#6B7280",
  marginBottom: theme.spacing(0.25),
}));

const MemberUsername = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  color: "#9CA3AF",
}));

const AdminBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: "#7C3AED",
  color: "#FFFFFF",
  fontSize: "0.8rem",
  height: "24px",
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const CreatedByBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: "#059669",
  color: "#FFFFFF",
  fontSize: "0.8rem",
  height: "24px",
  marginBottom: theme.spacing(0.5),
  fontWeight: 600,
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  "& .MuiIconButton-root": {
    backgroundColor: "#F3F4F6",
    color: "#6B7280",
    "&:hover": {
      backgroundColor: "#E5E7EB",
      color: "#374151",
    },
  },
}));

const MediaGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
    gap: theme.spacing(1),
  },
}));

const MediaItem = styled(Box)(({ theme }) => ({
  position: "relative",
  aspectRatio: "1",
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  cursor: "pointer",
  backgroundColor: "#F3F4F6",
  border: "1px solid #E5E7EB",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.02)",
    boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
    borderColor: "#7C3AED",
  },
}));

const MediaThumbnail = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const MediaIconOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  color: "#FFFFFF",
}));

const MediaInfo = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(0.5, 1),
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  color: "#FFFFFF",
  fontSize: "0.7rem",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const GroupInfo = ({ 
  isOpen, 
  onClose, 
  conversationId, 
  groupName, 
  isGroup,
  conversationInfo: passedConversationInfo,
}) => {
  
  const [activeTab, setActiveTab] = useState("info");
  const [conversationInfo, setConversationInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // States for member removal confirmation
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  
  // States for member options menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // State for user info dialog
  const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  
  // States for media
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPagination, setMediaPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  
  const { isConnected, emit, addEventListener } = useSocketContext();
  const currentUserId = useSelector((state) => state.auth.user?.userId);

  // Google Maps loader
  const googleMapKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
    googleMapsApiKey: googleMapKey || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Helper function to get initials from member name
  const getMemberInitials = (member) => {
    try {
      if (member.fullName && member.fullName.trim()) {
        const names = member.fullName.trim().split(' ').filter(name => name.length > 0);
        if (names.length === 1) {
          return names[0].charAt(0).toUpperCase();
        } else if (names.length >= 2) {
          return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
        }
      }
      if (member.userName && member.userName.trim()) {
        return member.userName.charAt(0).toUpperCase();
      }
      if (member.name && member.name.trim()) {
        return member.name.charAt(0).toUpperCase();
      }
      if (member.email && member.email.trim()) {
        // Extract name from email if available
        const emailName = member.email.split('@')[0];
        if (emailName && emailName.trim()) {
          return emailName.charAt(0).toUpperCase();
        }
      }
      return '?';
    } catch (error) {
      
      return '?';
    }
  };

  // Helper function to get display name for better fallbacks
  const getDisplayName = (member) => {
    try {
      if (member.fullName && member.fullName.trim()) {
        return member.fullName.trim();
      }
      if (member.userName && member.userName.trim()) {
        return member.userName.trim();
      }
      if (member.name && member.name.trim()) {
        return member.name.trim();
      }
      if (member.email && member.email.trim()) {
        const emailName = member.email.split('@')[0];
        if (emailName && emailName.trim()) {
          return emailName.trim();
        }
      }
      return 'Unknown User';
    } catch (error) {
      
      return 'Unknown User';
    }
  };

  // Helper function to handle image loading errors
  const handleImageError = (e, member) => {
    try {
      // Hide the broken image and show fallback
      e.target.style.display = 'none';
      e.target.nextSibling.style.display = 'flex';
      
    } catch (error) {
      
    }
  };

  // Helper function to get avatar background color based on name
  const getAvatarColor = (member) => {
    try {
      const displayName = getDisplayName(member);
      if (!displayName) return '#F3F4F6';
      
      const colors = [
        '#FEF3C7', // Light yellow
        '#DBEAFE', // Light blue
        '#D1FAE5', // Light green
        '#FCE7F3', // Light pink
        '#EDE9FE', // Light purple
        '#FEE2E2', // Light red
        '#E0E7FF', // Light indigo
        '#FEF3C7', // Light amber
        '#FEF2F2', // Light rose
        '#F0FDF4', // Light emerald
      ];
      
      const hash = displayName.split('').reduce((acc, char) => {
        return char.charCodeAt(0) + ((acc << 5) - acc);
      }, 0);
      
      return colors[Math.abs(hash) % colors.length];
    } catch (error) {
      
      return '#F3F4F6';
    }
  };

  // Helper function to get text color for better contrast
  const getTextColor = (backgroundColor) => {
    try {
      // Default to purple for good contrast
      return '#7C3AED';
    } catch (error) {
      
      return '#7C3AED';
    }
  };


  // Navigation tabs
  const navTabs = [
    { id: "info", label: "info" },
    { id: "media", label: "media, links" },
    { id: "members", label: "members" },
    { id: "banned", label: "banned" },
  ];

  const fetchConversationInfo = useCallback(() => {
    if (!isConnected || !conversationId) return;

    setIsLoading(true);
    
    
    // Emit getConversationsInfo event (note: plural 'conversations')
    emit("getConversationsInfo", { conversationId });
  }, [isConnected, conversationId, emit]);

  // Use passed conversation info if available, otherwise fetch it
  useEffect(() => {
    if (passedConversationInfo) {
      
      setConversationInfo(passedConversationInfo);
      setIsLoading(false);
    } else if (isOpen && isGroup && conversationId && isConnected) {
      fetchConversationInfo();
    }
  }, [isOpen, isGroup, conversationId, isConnected, passedConversationInfo, fetchConversationInfo]);

  // Reset media when conversation changes
  useEffect(() => {
    if (conversationId) {
      setMediaFiles([]);
      setMediaPagination({
        page: 1,
        limit: 50,
        total: 0,
        totalPages: 0,
        hasMore: false,
      });
    }
  }, [conversationId]);

  // Listen for conversation info response
  useEffect(() => {
    if (!isConnected) return;

    const handleConversationInfo = (data) => {
      
      if (data && data.conversations) {
        setConversationInfo(data.conversations);
        setIsLoading(false);
      } else {
        
        // Set a timeout to stop loading if no response
        setTimeout(() => {
          setIsLoading(false);
        }, 5000);
      }
    };

    // Add event listener for conversation info
    const unsubscribe = addEventListener("conversationsInfo", handleConversationInfo);

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isConnected, addEventListener]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleMemberAction = (action, member) => {
    if (action === "remove") {
      setMemberToRemove(member);
      setRemoveConfirmOpen(true);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, member) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedMember(null);
  };

  // Handle make admin
  const handleMakeAdmin = async () => {
    if (!selectedMember || !conversationId) return;

    try {
      setIsLoading(true);
      const payload = {
        conversationId,
        userId: selectedMember._id,
      };

      const response = await axiosInstance.post(
        ApiEndPoints.CONVERSATION.makeAdmin,
        payload
      );

      if (response.data.status) {
        toast.success(
          `${selectedMember.fullName || selectedMember.userName || "member"} is now an admin`
        );
        handleMenuClose();
        fetchConversationInfo(); // Refresh member list
      } else {
        toast.error(response.data.message || "failed to make admin");
      }
    } catch (error) {
      console.error("Error making admin:", error);
      toast.error(error.response?.data?.message || "something went wrong while making admin");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle remove admin
  const handleRemoveAdmin = async () => {
    if (!selectedMember || !conversationId) return;

    // Check if this is the last admin before attempting to remove
    const adminUserIds = getAdminUserIds();
    if (adminUserIds.length === 1 && adminUserIds.includes(selectedMember._id)) {
      toast.error("cannot remove the last admin from the group");
      handleMenuClose();
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        conversationId,
        userId: selectedMember._id,
      };

      const response = await axiosInstance.post(
        ApiEndPoints.CONVERSATION.removeAdmin,
        payload
      );

      if (response.data.status) {
        toast.success(
          `${selectedMember.fullName || selectedMember.userName || "member"} is no longer an admin`
        );
        handleMenuClose();
        fetchConversationInfo(); // Refresh member list
      } else {
        toast.error(response.data.message || "failed to remove admin");
      }
    } catch (error) {
      console.error("Error removing admin:", error);
      toast.error(error.response?.data?.message || "something went wrong while removing admin");
    } finally {
      setIsLoading(false);
    }
  };


  const handleConfirmRemoveMember = async () => {
    if (!memberToRemove || !conversationId) return;

    try {
      setIsLoading(true);
      const payload = {
        conversationId,
        userId: memberToRemove._id,
      };

      const response = await axiosInstance.post(
        ApiEndPoints.CONVERSATION.removeMember,
        payload
      );

      if (response.data.status) {
        toast.success(
          `${memberToRemove.fullName || memberToRemove.userName || "member"} removed successfully`
        );
        setRemoveConfirmOpen(false);
        setMemberToRemove(null);
        fetchConversationInfo(); // Refresh member list
      } else {
        toast.error(response.data.message || "failed to remove member");
      }
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error(error.response?.data?.message || "something went wrong while removing member");
    } finally {
      setIsLoading(false);
    }
  };

  // Get admin user IDs
  const getAdminUserIds = () => {
    if (!conversationInfo) return [];
    const { admin = [] } = conversationInfo;
    return admin.map(adminMember => adminMember?.userId?._id).filter(Boolean);
  };

  // Check if current user is admin
  const isCurrentUserAdmin = () => {
    if (!conversationInfo || !currentUserId) return false;
    const adminUserIds = getAdminUserIds();
    return adminUserIds.includes(currentUserId);
  };

  // Handle edit dialog open
  const handleEditOpen = () => {
    setEditDialogOpen(true);
  };

  // Handle edit dialog close
  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  // Handle edit success - refresh conversation info
  const handleEditSuccess = () => {
    if (isConnected && conversationId) {
      fetchConversationInfo();
    }
  };

  // Fetch media files
  const fetchMediaFiles = useCallback(async (page = 1, limit = 50) => {
    if (!conversationId) return;

    try {
      setIsLoadingMedia(true);
      const response = await axiosInstance.get(
        ApiEndPoints.CONVERSATION.getMedia(conversationId, { page, limit })
      );

      if (response.data.status && response.data.data) {
        const { messages, pagination } = response.data.data;
        if (page === 1) {
          setMediaFiles(messages || []);
        } else {
          setMediaFiles((prev) => [...prev, ...(messages || [])]);
        }
        setMediaPagination(pagination || {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0,
          hasMore: false,
        });
      } else {
        toast.error(response.data.message || "failed to fetch media files");
      }
    } catch (error) {
      console.error("Error fetching media files:", error);
      toast.error(error.response?.data?.message || "something went wrong while fetching media");
    } finally {
      setIsLoadingMedia(false);
    }
  }, [conversationId]);

  // Load media when media tab is active
  useEffect(() => {
    if (activeTab === "media" && conversationId && mediaFiles.length === 0) {
      fetchMediaFiles(1, 50);
    }
  }, [activeTab, conversationId, fetchMediaFiles, mediaFiles.length]);

  // Handle media item click
  const handleMediaClick = (mediaIndex) => {
    setSelectedMediaIndex(mediaIndex);
    setMediaViewerOpen(true);
  };

  // Get media URL helper
  const getMediaUrl = (url, type = "image") => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    
    // Use appropriate base URL based on media type
    switch (type) {
      case "video":
        return `${VIDEO_URL}${url}`;
      case "audio":
        return `${AUDIO_URL}${url}`;
      case "image":
      default:
        return `${MEDIA_URL}${url}`;
    }
  };

  // Get media type icon
  const getMediaTypeIcon = (type) => {
    switch (type) {
      case "image":
        return <ImageIcon sx={{ fontSize: 32 }} />;
      case "video":
        return <VideoIcon sx={{ fontSize: 32 }} />;
      case "audio":
        return <AudioIcon sx={{ fontSize: 32 }} />;
      case "document":
        return <DocumentIcon sx={{ fontSize: 32 }} />;
      default:
        return <FileIcon sx={{ fontSize: 32 }} />;
    }
  };

  const renderMembersTab = () => {
    try {
      if (!conversationInfo) {
        return (
          <Box>
            <SectionTitle>group members</SectionTitle>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              member information is loading or unavailable
            </Typography>
            <Typography variant="body2" color="text.secondary">
              conversation id: {conversationId}
            </Typography>
          </Box>
        );
      }

      const { admin = [], users = [] } = conversationInfo;
      

      // Combine all members from both admin and users arrays, removing duplicates
      const allMembers = [...admin, ...users];
      
      // Remove duplicates based on user ID
      const uniqueMembers = allMembers.filter((member, index, self) => {
        const userId = member?.userId?._id;
        return userId && self.findIndex(m => m?.userId?._id === userId) === index;
      });
      
      // Get admin user IDs for checking admin status
      const adminUserIds = getAdminUserIds();
      
      // Separate members by type using userType field
      // Owners: only those with userType === "owner"
      const owners = uniqueMembers.filter(member => {
        return member?.userType === "owner";
      });
      
      // Users: those with userType === "user"
      const userMembers = uniqueMembers.filter(member => {
        return member?.userType === "user";
      });
      
      const teamMembers = uniqueMembers.filter(member => {
        // Include team members and beemz ai in team members section
        const userName = member?.userId?.userName?.toLowerCase();
        const isBeemzAi = userName === "beemzai" || userName === "beemz ai";
        return member?.userType === "teamMember" || member?.userType === "ai" || isBeemzAi;
      });
      
      const otherUsers = uniqueMembers.filter(member => {
        // Exclude beemz ai and AI users from group members - they should be in team members
        const userName = member?.userId?.userName?.toLowerCase();
        const isBeemzAi = userName === "beemzai" || userName === "beemz ai";
        const isOwner = member?.userType === "owner";
        const isUser = member?.userType === "user";
        const isTeamMember =
          member?.userType === "teamMember" || member?.userType === "ai" || isBeemzAi;
        // Treat missing userType as "user" (common for event group members) but exclude if already categorized
        const isUserType = !member?.userType;
        return isUserType && !isTeamMember && !isOwner && !isUser;
      });

      const createdById = conversationInfo?.createdBy?._id;

      const renderMemberItem = (member, index) => {
        try {
          const user = member?.userId;
          if (!user) return null;
          
          const isCurrentUser = user._id === currentUserId;
          const isAdmin = adminUserIds.includes(user._id);
          const isCreatedBy = createdById && String(user._id) === String(createdById);
          
          return (
            <MemberItem key={member._id || index}>
              <MemberAvatar>
                {user.image?.profilePic ? (
                  <>
                    <img 
                      src={IMAGE_URL + user.image.profilePic} 
                      alt={user.fullName || 'User avatar'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => handleImageError(e, user)}
                    />
                    <Box 
                      className="fallback-initials" 
                      style={{ display: 'none' }}
                    >
                      {getMemberInitials(user)}
                    </Box>
                  </>
                ) : (
                  <Box 
                    className="fallback-initials"
                    role="img"
                    aria-label={`Avatar for ${user.fullName || user.userName || 'user'} - ${getMemberInitials(user)} initials`}
                    sx={{ 
                      backgroundColor: getAvatarColor(user),
                      color: getTextColor(getAvatarColor(user))
                    }}
                  >
                    {getMemberInitials(user)}
                  </Box>
                )}
              </MemberAvatar>
              <MemberInfo>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <MemberName>
                    {isCurrentUser ? "you" : user.fullName || user.userName || 'Unknown User'}
                  </MemberName>
                  {isCreatedBy && (
                    <CreatedByBadge label="created by" size="small" />
                  )}
                  {isAdmin && (
                    <AdminBadge label="admin" size="small" />
                  )}
                </Box>
                <MemberSubtitle>
                  {user.userName || "username"}
                </MemberSubtitle>
                {/* Show designation for team members and owners, not for normal users */}
                {(member.userType === "owner" || member.userType === "teamMember") && user.designation && (
                  <Chip
                    label={user.designation}
                    size="small"
                    sx={{
                      backgroundColor: member.userType === "owner" ? "#10B981" : "#3B82F6",
                      color: "#FFFFFF",
                      fontSize: "0.75rem",
                      height: "20px",
                      fontWeight: 600,
                      marginBottom: 0.5,
                      "& .MuiChip-label": {
                        padding: "0 6px",
                      },
                    }}
                  />
                )}
              </MemberInfo>
              {!isCurrentUser && isCurrentUserAdmin() && (
                <ListItemSecondaryAction>
                  <ActionButtons>
                    <IconButton
                      size="small"
                      onClick={() => handleMemberAction("remove", user)}
                      sx={{ color: "#6B7280" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, user)}
                      sx={{ color: "#6B7280" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </ActionButtons>
                </ListItemSecondaryAction>
              )}
            </MemberItem>
          );
        } catch (error) {
          
          return null;
        }
      };

      return (
        <Box>
          {/* Owner Section - Top */}
          {owners.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SectionTitle>owner</SectionTitle>
              <List>
                {owners.map((owner, index) => renderMemberItem(owner, index))}
              </List>
            </Box>
          )}

          {/* Team Members Section - Second */}
          {teamMembers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SectionTitle>team members</SectionTitle>
              <List>
                {teamMembers.map((member, index) => renderMemberItem(member, index))}
              </List>
            </Box>
          )}

          {/* Users Section - Last */}
          {userMembers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <SectionTitle>users</SectionTitle>
              <List>
                {userMembers.map((user, index) => renderMemberItem(user, index))}
              </List>
            </Box>
          )}

          {/* Other Users Section */}
          {otherUsers.length > 0 && (
            <Box>
              <SectionTitle>group members</SectionTitle>
              <List>
                {otherUsers.map((member, index) => renderMemberItem(member, index))}
              </List>
            </Box>
          )}
        </Box>
      );
    } catch (error) {
      
      return (
        <Box>
          <SectionTitle>group members</SectionTitle>
          <Typography variant="body1" color="error" sx={{ mb: 2 }}>
            error loading member information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            please try again later
          </Typography>
        </Box>
      );
    }
  };

  const renderMediaTab = () => {
    const getMediaThumbnail = (message) => {
      if (!message.attachments || message.attachments.length === 0) return null;
      const attachment = message.attachments[0];
      
      if (attachment.type === "image") {
        return getMediaUrl(attachment.url || attachment.thumbnail, "image");
      } else if (attachment.type === "video") {
        return getMediaUrl(attachment.thumbnail, "image") || getMediaUrl(attachment.url, "video");
      }
      return null;
    };

    const getMediaType = (message) => {
      if (!message.attachments || message.attachments.length === 0) return "file";
      return message.attachments[0].type || "file";
    };

    const getSenderName = (message) => {
      if (message.senderId) {
        if (message.senderId._id === currentUserId) {
          return "you";
        }
        return message.senderId.fullName || message.senderId.userName || "Unknown";
      }
      return "Unknown";
    };

    if (isLoadingMedia && mediaFiles.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress size={48} sx={{ color: "#7C3AED" }} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            loading media files...
          </Typography>
        </Box>
      );
    }

    if (mediaFiles.length === 0) {
      return (
        <Box>
          <SectionTitle>media and links</SectionTitle>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            no media files found in this conversation
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <SectionTitle>media and links</SectionTitle>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {mediaPagination.total} {mediaPagination.total === 1 ? "file" : "files"}
        </Typography>

        <MediaGrid>
          {mediaFiles.map((message, index) => {
            const thumbnail = getMediaThumbnail(message);
            const mediaType = getMediaType(message);
            const senderName = getSenderName(message);

            return (
              <MediaItem
                key={message._id || index}
                onClick={() => handleMediaClick(index)}
              >
                {thumbnail ? (
                  <MediaThumbnail
                    src={thumbnail}
                    alt={message.attachments?.[0]?.fileName || "media"}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <MediaIconOverlay>
                    {getMediaTypeIcon(mediaType)}
                  </MediaIconOverlay>
                )}
                <MediaInfo>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#FFFFFF",
                      fontSize: "0.65rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {senderName}
                  </Typography>
                </MediaInfo>
                {mediaType === "video" && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#FFFFFF",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      borderRadius: "50%",
                      padding: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PlayIcon />
                  </Box>
                )}
              </MediaItem>
            );
          })}
        </MediaGrid>

        {mediaPagination.hasMore && (
          <PaginationContainer>
            <Button
              onClick={() => fetchMediaFiles(mediaPagination.page + 1, mediaPagination.limit)}
              disabled={isLoadingMedia}
              variant="outlined"
              sx={{
                color: "#7C3AED",
                borderColor: "#7C3AED",
                textTransform: "lowercase",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6D28D9",
                  backgroundColor: "rgba(124, 58, 237, 0.04)",
                },
              }}
            >
              {isLoadingMedia ? (
                <CircularProgress size={20} sx={{ color: "#7C3AED" }} />
              ) : (
                "load more"
              )}
            </Button>
          </PaginationContainer>
        )}

        {mediaPagination.totalPages > 1 && (
          <PaginationContainer>
            <Typography variant="body2" color="text.secondary">
              page {mediaPagination.page} of {mediaPagination.totalPages}
            </Typography>
          </PaginationContainer>
        )}
      </Box>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "members":
        return renderMembersTab();
      case "info":
        return (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <SectionTitle sx={{ mb: 0 }}>group information</SectionTitle>
              {isCurrentUserAdmin() && (
                <IconButton
                  onClick={handleEditOpen}
                  sx={{
                    color: "#7C3AED",
                    backgroundColor: "#F3F4F6",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              )}
            </Box>
            {conversationInfo ? (
              <Box>
                {/* Group Profile Image */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                  <Avatar
                    src={
                      conversationInfo.groupProfile
                        ? `${MEDIA_URL}${conversationInfo.groupProfile}`
                        : null
                    }
                    sx={{
                      width: 120,
                      height: 120,
                      border: "4px solid #7C3AED",
                      boxShadow: "0 4px 12px rgba(124, 58, 237, 0.2)",
                      bgcolor: "#E5E7EB",
                    }}
                    imgProps={{
                      onError: (e) => {
                        e.target.style.display = 'none';
                      }
                    }}
                  >
                    {conversationInfo.groupName?.charAt(0)?.toUpperCase() || "G"}
                  </Avatar>
                </Box>

                {/* Group Name */}
                <Box sx={{ mb: 3, textAlign: "center" }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700, 
                      color: "#1F2937",
                      mb: 0.5
                    }}
                  >
                    {conversationInfo.groupName}
                  </Typography>
                  {conversationInfo.groupType && (
                    <Chip
                      label={conversationInfo.groupType}
                      size="small"
                      sx={{
                        backgroundColor: "#7C3AED",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        textTransform: "capitalize",
                      }}
                    />
                  )}
                </Box>

                {/* Description */}
                {conversationInfo.description && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: "#F9FAFB", borderRadius: "8px" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#374151" }}>
                      description
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", lineHeight: 1.6 }}>
                      {conversationInfo.description}
                    </Typography>
                  </Box>
                )}

                {/* Subject */}
                {conversationInfo.subject && (
                  <Box sx={{ mb: 3, p: 2, backgroundColor: "#F9FAFB", borderRadius: "8px" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#374151" }}>
                      subject
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", lineHeight: 1.6 }}>
                      {conversationInfo.subject}
                    </Typography>
                  </Box>
                )}

                {/* Location Section */}
                {(conversationInfo.address || conversationInfo.latLong) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}>
                      location
                    </Typography>
                    
                    {/* Address */}
                    {conversationInfo.address && (
                      <Box sx={{ mb: 2, p: 2, backgroundColor: "#F9FAFB", borderRadius: "8px" }}>
                        <Typography variant="body2" sx={{ color: "#6B7280", lineHeight: 1.6 }}>
                          {conversationInfo.address}
                        </Typography>
                      </Box>
                    )}

                    {/* Map Card */}
                    {conversationInfo.latLong && (
                      <Box
                        sx={{
                          borderRadius: "12px",
                          overflow: "hidden",
                          border: "1px solid #E5E7EB",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                          mb: 2,
                          backgroundColor: "#FFFFFF",
                        }}
                      >
                        {mapLoadError ? (
                          <Box
                            sx={{
                              height: "200px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#F3F4F6",
                            }}
                          >
                            <Typography variant="body2" sx={{ color: "#6B7280" }}>
                              error loading map
                            </Typography>
                          </Box>
                        ) : !isMapLoaded ? (
                          <Box
                            sx={{
                              height: "200px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#F3F4F6",
                            }}
                          >
                            <CircularProgress size={24} sx={{ color: "#7C3AED" }} />
                          </Box>
                        ) : (
                          <GoogleMap
                            mapContainerStyle={{
                              width: "100%",
                              height: "200px",
                            }}
                            center={{
                              lat: conversationInfo.latLong.lat,
                              lng: conversationInfo.latLong.long,
                            }}
                            zoom={14}
                            options={{
                              streetViewControl: false,
                              scaleControl: false,
                              mapTypeControl: false,
                              panControl: false,
                              zoomControl: true,
                              rotateControl: false,
                              fullscreenControl: false,
                              disableDefaultUI: false,
                            }}
                          >
                            <Marker
                              position={{
                                lat: conversationInfo.latLong.lat,
                                lng: conversationInfo.latLong.long,
                              }}
                              title={conversationInfo.address || "Group location"}
                            />
                          </GoogleMap>
                        )}
                        <Box sx={{ p: 1.5, backgroundColor: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid #E5E7EB" }}>
                          <Typography variant="caption" sx={{ color: "#7C3AED", fontWeight: 500 }}>
                            group location
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.7rem" }}>
                            {conversationInfo.latLong.lat.toFixed(4)}, {conversationInfo.latLong.long.toFixed(4)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Business Location Info */}
                {conversationInfo.businessLocationId && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "#374151" }}>
                      business information
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "#F9FAFB", borderRadius: "8px" }}>
                      {conversationInfo.businessLocationId.businessLocationName && (
                        <Typography variant="body2" sx={{ mb: 1, color: "#1F2937", fontWeight: 500 }}>
                          {conversationInfo.businessLocationId.businessLocationName}
                        </Typography>
                      )}
                      {conversationInfo.businessLocationId.physicalAddress && (
                        <Typography variant="body2" sx={{ mb: 1, color: "#6B7280" }}>
                          {conversationInfo.businessLocationId.physicalAddress}
                        </Typography>
                      )}
                      {conversationInfo.businessLocationId.emailAddress && (
                        <Typography variant="body2" sx={{ color: "#6B7280" }}>
                          {conversationInfo.businessLocationId.emailAddress}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {/* Chat Category */}
                {conversationInfo.chatCategory && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#374151" }}>
                      chat category
                    </Typography>
                    <Chip
                      label={conversationInfo.chatCategory.name}
                      size="small"
                      sx={{
                        backgroundColor: "#EDE9FE",
                        color: "#7C3AED",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                )}

                {/* Additional Info */}
                <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #E5E7EB" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 500 }}>
                      created
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {new Date(conversationInfo.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 500 }}>
                      created by
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      {conversationInfo.createdBy?.fullName || conversationInfo.createdBy?.userName || 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>group name:</strong> {groupName || "unknown"}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>conversation id:</strong> {conversationId}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  group information is loading or unavailable
                </Typography>
              </Box>
            )}
          </Box>
        );
      case "media":
        return renderMediaTab();
      case "banned":
        return (
          <Box>
            <SectionTitle>banned members</SectionTitle>
            <Typography variant="body1" color="text.secondary">
              banned members will be displayed here
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  if (!isOpen || !isGroup) return null;

  try {
    return (
      <>
        <Backdrop onClick={onClose} />
        <GroupInfoDialog>
          {/* Header */}
          <GroupInfoHeader>
            <GroupInfoTitle>group info</GroupInfoTitle>
            <HeaderActions>
              {/* <IconButton
                size="small"
                onClick={onFullscreen}
                title="fullscreen"
              >
                <FullscreenIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onMinimize}
                title="minimize"
              >
                <MinimizeIcon fontSize="small" />
              </IconButton> */}
              <IconButton
                size="small"
                onClick={onClose}
                title="close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </HeaderActions>
          </GroupInfoHeader>

          {/* Content */}
          <ContentContainer>
            {/* Navigation Sidebar */}
            <NavigationSidebar>
              {navTabs.map((tab) => (
                <NavItem
                  key={tab.id}
                  active={activeTab === tab.id}
                  onClick={() => handleTabChange(tab.id)}
                >
                  {tab.label}
                </NavItem>
              ))}
            </NavigationSidebar>

            {/* Main Content */}
            <MainContent>
              {isLoading ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Box sx={{ mb: 2 }}>
                    <CircularProgress size={48} sx={{ color: "#7C3AED" }} />
                  </Box>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    loading group information...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    please wait while we fetch the details
                  </Typography>
                </Box>
              ) : (
                renderContent()
              )}
            </MainContent>
          </ContentContainer>
        </GroupInfoDialog>

        {/* Edit Group Dialog */}
        <EditGroupDialog
          open={editDialogOpen}
          onClose={handleEditClose}
          conversationId={conversationId}
          conversationInfo={conversationInfo}
          onSuccess={handleEditSuccess}
        />

        {/* Remove Member Confirmation Dialog */}
        <Dialog
          open={removeConfirmOpen}
          onClose={() => !isLoading && setRemoveConfirmOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "8px",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            remove member?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: "#4B5563" }}>
              are you sure you want to remove{" "}
              <strong>
                {memberToRemove?.fullName || memberToRemove?.userName || "this member"}
              </strong>{" "}
              from the group? they will no longer be able to see or send messages.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setRemoveConfirmOpen(false)}
              disabled={isLoading}
              sx={{
                color: "#ffffffff",
                textTransform: "lowercase",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              cancel
            </Button>
            <Button
              onClick={handleConfirmRemoveMember}
              disabled={isLoading}
              variant="contained"
              sx={{
                backgroundColor: "#EF4444",
                color: "#FFFFFF",
                textTransform: "lowercase",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "#DC2626",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#FECACA",
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
              ) : (
                "remove"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Member Options Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: "8px",
              minWidth: "180px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {selectedMember && (() => {
            const adminUserIds = getAdminUserIds();
            const isSelectedMemberAdmin = adminUserIds.includes(selectedMember._id);
            return (
              <>
                {isSelectedMemberAdmin ? (
                  <MenuItem
                    onClick={handleRemoveAdmin}
                    disabled={isLoading}
                    sx={{
                      fontSize: "0.875rem",
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#F3F4F6",
                      },
                    }}
                  >
                    remove admin
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={handleMakeAdmin}
                    disabled={isLoading}
                    sx={{
                      fontSize: "0.875rem",
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#F3F4F6",
                      },
                    }}
                  >
                    make admin
                  </MenuItem>
                )}
              </>
            );
          })()}
        </Menu>

        {/* User Info Dialog */}
        <Dialog
          open={userInfoDialogOpen}
          onClose={() => setUserInfoDialogOpen(false)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              maxWidth: "500px",
              width: "90vw",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <InfoIcon sx={{ color: "#7C3AED" }} />
            user information
          </DialogTitle>
          <DialogContent>
            {userInfo ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <Avatar
                    src={
                      userInfo.image?.profilePic
                        ? `${IMAGE_URL}${userInfo.image.profilePic}`
                        : null
                    }
                    sx={{
                      width: 64,
                      height: 64,
                      border: "3px solid #7C3AED",
                    }}
                  >
                    {userInfo.fullName?.charAt(0)?.toUpperCase() || userInfo.userName?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {userInfo.fullName || userInfo.userName || "Unknown User"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      @{userInfo.userName || "username"}
                    </Typography>
                  </Box>
                </Box>

                {userInfo.email && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#374151" }}>
                      email
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      {userInfo.email}
                    </Typography>
                  </Box>
                )}

                {userInfo.mobile && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#374151" }}>
                      mobile
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      {userInfo.mobile}
                    </Typography>
                  </Box>
                )}

                {userInfo.bio && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#374151" }}>
                      bio
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      {userInfo.bio}
                    </Typography>
                  </Box>
                )}

                {userInfo.userStatus && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: "#374151" }}>
                      status
                    </Typography>
                    <Chip
                      label={userInfo.userStatus}
                      size="small"
                      sx={{
                        backgroundColor: userInfo.userStatus === "available" ? "#10B981" : "#6B7280",
                        color: "#FFFFFF",
                        textTransform: "capitalize",
                      }}
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                loading user information...
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setUserInfoDialogOpen(false)}
              sx={{
                color: "#7C3AED",
                textTransform: "lowercase",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Media Viewer Dialog */}
        <Dialog
          open={mediaViewerOpen}
          onClose={() => setMediaViewerOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              maxHeight: "90vh",
              backgroundColor: "#000000",
            },
          }}
        >
          {selectedMediaIndex !== null && mediaFiles[selectedMediaIndex] && (() => {
            const message = mediaFiles[selectedMediaIndex];
            const attachment = message.attachments?.[0];
            const mediaType = attachment?.type || "file";
            const mediaUrl = getMediaUrl(attachment?.url, mediaType);
            const thumbnailUrl = getMediaUrl(attachment?.thumbnail, "image");
            const senderName = message.senderId?._id === currentUserId
              ? "you"
              : message.senderId?.fullName || message.senderId?.userName || "Unknown";

            return (
              <>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "400px",
                    maxHeight: "70vh",
                    backgroundColor: "#000000",
                    p: 2,
                  }}
                >
                  <IconButton
                    onClick={() => setMediaViewerOpen(false)}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "#FFFFFF",
                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                      zIndex: 1,
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  {mediaType === "image" && (
                    <img
                      src={mediaUrl}
                      alt={attachment?.fileName || "image"}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        objectFit: "contain",
                      }}
                    />
                  )}

                  {mediaType === "video" && (
                    <video
                      src={mediaUrl}
                      controls
                      autoPlay
                      style={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                      }}
                    >
                      your browser does not support the video tag.
                    </video>
                  )}

                  {mediaType === "audio" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        color: "#FFFFFF",
                        p: 4,
                      }}
                    >
                      <AudioIcon sx={{ fontSize: 64 }} />
                      <audio
                        src={mediaUrl}
                        controls
                        style={{
                          width: "100%",
                          maxWidth: "400px",
                        }}
                      />
                    </Box>
                  )}

                  {(mediaType === "document" || mediaType === "file") && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        color: "#FFFFFF",
                        p: 4,
                      }}
                    >
                      <FileIcon sx={{ fontSize: 64 }} />
                      <Typography variant="h6" sx={{ color: "#FFFFFF" }}>
                        {attachment?.fileName || "file"}
                      </Typography>
                      <Button
                        variant="contained"
                        href={mediaUrl}
                        download={attachment?.fileName}
                        sx={{
                          backgroundColor: "#7C3AED",
                          color: "#FFFFFF",
                          "&:hover": {
                            backgroundColor: "#6D28D9",
                          },
                        }}
                      >
                        download
                      </Button>
                    </Box>
                  )}

                  {mediaFiles.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => {
                          const prevIndex = selectedMediaIndex > 0
                            ? selectedMediaIndex - 1
                            : mediaFiles.length - 1;
                          setSelectedMediaIndex(prevIndex);
                        }}
                        sx={{
                          position: "absolute",
                          left: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#FFFFFF",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                          },
                        }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          const nextIndex = selectedMediaIndex < mediaFiles.length - 1
                            ? selectedMediaIndex + 1
                            : 0;
                          setSelectedMediaIndex(nextIndex);
                        }}
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#FFFFFF",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                          },
                        }}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#1F2937",
                    borderTop: "1px solid #374151",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 0.5 }}>
                    sent by {senderName}
                  </Typography>
                  {attachment?.fileName && (
                    <Typography variant="body2" sx={{ color: "#6B7280" }}>
                      {attachment.fileName}
                    </Typography>
                  )}
                  {message.createdAt && (
                    <Typography variant="caption" sx={{ color: "#6B7280" }}>
                      {new Date(message.createdAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </>
            );
          })()}
        </Dialog>
      </>
    );
  } catch (error) {
    
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          error loading group information
        </Typography>
        <Typography variant="body2" color="text.secondary">
          please try again later
        </Typography>
      </Box>
    );
  }
};

export default GroupInfo;
